import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Flag, Quote, ShieldCheck } from "lucide-react";
import { Logo } from "@/components/Logo";

const STUDENT_ART = "https://static.prod-images.emergentagent.com/jobs/0c62e012-1fb3-4426-a0f5-6bb9c23d9db4/images/6c9eb3bba7534bb0ac924d8dde8b5537fd9454aa566776030591a7da45ab7f3d.jpeg";

const shots = [
  ["/shots/onboarding.jpg", "Setting up", "Three steps. Name and date of birth, where you're really starting from, then your goal in your own words. Everything after your name is skippable."],
  ["/shots/curriculum.jpg", "Your curriculum", "Subject → Chapter → Topic, drafted from your goal. Add a subject, drop a chapter — the advisor rewrites the topics inside it."],
  ["/shots/dashboard.jpg", "Today", "The planner shapes each day around your real pace and tells you honestly whether you're ahead or behind."],
  ["/shots/progress.jpg", "Progress", "What's done, what's next, and where the difficulty actually is."],
];

const beliefs = [
  ["The learner owns the journey", "No plan needs anyone's approval. You choose the subjects, the depth and the pace — and you can change all of it tomorrow."],
  ["Wherever you start is fine", "Never been to school? Took three years off? Say so. Your age sets the language, your level sets the content, and the two are never confused."],
  ["Sourced, not invented", "References come from real retrieval — arXiv, Open Library, encyclopaedic APIs. If the material cites something, that something exists."],
  ["A guardian is welcome, not required", "Invite one for a read-only view and a hand when you're stuck, or don't. Both are first-class."],
];

export default function About() {
  return (
    <div className="min-h-screen grain">
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="about-root">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-5">
            <Logo size="md" testId="about-logo" />
            <Link to="/" className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900">
              <ArrowLeft className="w-4 h-4" /> Back
            </Link>
          </div>
          <Link to="/signup" className="px-5 py-2 text-sm font-semibold bg-slate-900 text-white rounded-full hover:bg-slate-800 transition-colors">
            Start learning
          </Link>
        </div>

        <section className="mt-12 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="rule-label">About Unbound</p>
            <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold leading-[1.06]">
              A school that starts with the student, not the syllabus.
            </h1>
            <p className="mt-6 text-base text-slate-600 leading-relaxed max-w-2xl">
              Most education begins with a fixed curriculum and asks the learner to fit it. Unbound begins with a goal in
              the learner's own words — then builds the curriculum backwards from there, teaches it one idea at a time,
              and adapts when something doesn't land. Ages 6 to 18, formal schooling optional.
            </p>
          </div>
          <div className="lg:col-span-5">
            <img src={STUDENT_ART} alt="A student studying surrounded by floating ideas" className="w-full rounded-3xl border border-slate-200 object-cover aspect-[4/3]" data-testid="about-hero-image" />
          </div>
        </section>

        <section className="mt-20">
          <p className="rule-label">What we believe</p>
          <div className="mt-6 grid sm:grid-cols-2 gap-5">
            {beliefs.map(([title, note]) => (
              <div key={title} className="card-flat p-6">
                <h3 className="font-display text-lg font-semibold">{title}</h3>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">{note}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-20" data-testid="about-shots">
          <p className="rule-label">Inside the platform</p>
          <h2 className="mt-3 text-3xl font-extrabold">Real screens, not mockups</h2>
          <div className="mt-8 space-y-12">
            {shots.map(([src, title, note], i) => (
              <figure key={src} className={`grid lg:grid-cols-12 gap-6 items-center ${i % 2 ? "lg:[direction:rtl]" : ""}`}>
                <div className="lg:col-span-8 lg:[direction:ltr]">
                  <img src={src} alt={title} className="w-full rounded-2xl border border-slate-200 shadow-[0_20px_60px_-40px_rgba(15,23,42,0.5)]" loading="lazy" />
                </div>
                <figcaption className="lg:col-span-4 lg:[direction:ltr]">
                  <h3 className="font-display text-xl font-semibold">{title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{note}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="mt-20 grid lg:grid-cols-2 gap-5">
          <div className="card-flat p-7">
            <ShieldCheck className="w-5 h-5 text-blue-600" />
            <h3 className="mt-4 font-display text-xl font-semibold">Honest about AI</h3>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
              Every lesson is written by AI models grounded in retrieved sources. That makes them fast, patient and
              endlessly re-explainable — and occasionally wrong. So every card can be sent back with "explain again",
              every question gets answered, and anything that still doesn't land goes onto your own stuck list instead of
              being quietly forgotten.
            </p>
          </div>
          <div className="card-flat p-7">
            <Flag className="w-5 h-5 text-blue-600" />
            <h3 className="mt-4 font-display text-xl font-semibold">Free, and built to stay that way</h3>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
              No fees and no card. Generation is metered with a generous daily and monthly allowance per learner, plus a
              platform ceiling — so a handful of heavy users can never take the platform down for a learner who has one
              shot at studying today. Read the{" "}
              <Link to="/terms" className="font-semibold text-blue-700 hover:underline">terms</Link> and the{" "}
              <Link to="/privacy" className="font-semibold text-blue-700 hover:underline">privacy policy</Link> — both
              written to be read.
            </p>
          </div>
        </section>

        <section className="mt-20 py-16 border-t border-slate-200 text-center">
          <Quote className="w-5 h-5 mx-auto text-blue-600" />
          <p className="mt-4 font-display text-2xl sm:text-3xl font-extrabold max-w-2xl mx-auto leading-snug">
            Do, or do not. There is no try — only the next topic.
          </p>
          <div className="mt-9">
            <Link to="/signup" data-testid="about-cta" className="inline-flex items-center gap-2 px-7 py-3.5 bg-blue-600 text-white rounded-full font-semibold hover:bg-blue-700 transition-colors">
              Set your goal <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="mt-14 pt-8 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4">
            <Logo testId="about-logo-footer" />
            <p className="text-xs text-slate-400">Learning belongs to the learner.</p>
          </div>
        </section>
      </div>
    </div>
  );
}
